/**
 * The cost band toggles above the model catalog (#2011).
 *
 * Four buttons — Low / Medium / High / Premium — that filter the table in one
 * click. Reaching the same thing through the filter builder takes four
 * interactions (open the field picker, choose Cost, choose a value, press Add
 * filter), and nothing on the screen suggested the bands existed even though
 * every row already showed one in its Cost column.
 *
 * **This component owns no filter state.** It is handed the current selection
 * and reports the next one; the page writes it into the same condition list the
 * chips and the filter builder use. That is deliberate — two sources of truth
 * for one filter is how a chip row comes to disagree with the table, and it
 * would make "Clear all" lie.
 *
 * Words, colours and the ✦ scale are imported from `CostCategoryPill` rather
 * than restated, so a toggle and the Cost column can never describe the same
 * band differently.
 */
import type { ModelPricingRow } from '@/lib/api'
import {
  COST_CATEGORY_ORDER,
  COST_CATEGORY_LABEL,
  COST_CATEGORY_STYLE,
  CostCategoryGlyphs,
} from '@/components/CostCategoryPill'

type CostCategory = NonNullable<ModelPricingRow['costCategory']>

export interface CostBandTogglesProps {
  /** Bands currently selected. Empty means no cost condition is active. */
  selected: readonly CostCategory[]
  /** How many models each band would show if it were clicked — see
   * `countBandsExcludingCost` in the page for why that is not a global count. */
  counts: Readonly<Record<CostCategory, number>>
  /** Hands back the full next selection rather than the band that changed, so
   * the caller never has to reproduce the toggle rule to interpret it. */
  onChange: (next: CostCategory[]) => void
}

export function CostBandToggles({ selected, counts, onChange }: CostBandTogglesProps) {
  /**
   * Every selection this component reports is rebuilt from the canonical
   * cheapest→priciest order, so the chip reads "Low or Medium" and never
   * "Medium or Low".
   *
   * Rebuilt on BOTH paths, including removal. Filtering the incoming array
   * would have preserved whatever order it arrived in, and it does not always
   * arrive sorted: the filter builder appends each value as its box is ticked
   * (`[...prev, value]`), so picking Medium before Low hands us
   * `['medium','low']`. Toggling a third band off would then have carried that
   * order straight back out (PR #2013 Copilot).
   */
  const toggle = (band: CostCategory) => {
    const next = COST_CATEGORY_ORDER.filter(b =>
      b === band ? !selected.includes(band) : selected.includes(b),
    )
    onChange(next)
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="cost-band-toggles">
      <span className="text-sm">Cost</span>
      {COST_CATEGORY_ORDER.map(band => {
        const on = selected.includes(band)
        const count = counts[band] ?? 0
        // A band that would show nothing is not selectable. Left visible, and
        // greyed rather than removed, so the four-band scale stays legible —
        // a row that changes shape as you filter is harder to read than one
        // with a quiet entry in it.
        const empty = count === 0 && !on
        return (
          <button
            key={band}
            type="button"
            disabled={empty}
            aria-pressed={on}
            title={
              empty
                ? `No ${COST_CATEGORY_LABEL[band].toLowerCase()}-cost models match the other filters`
                : `Show only ${COST_CATEGORY_LABEL[band].toLowerCase()}-cost models`
            }
            onClick={() => toggle(band)}
            className={[
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
              on
                ? `${COST_CATEGORY_STYLE[band]} border-transparent`
                : 'bg-background text-muted-foreground hover:bg-muted',
              empty ? 'opacity-40 cursor-not-allowed' : '',
            ].join(' ')}
          >
            <span>{COST_CATEGORY_LABEL[band]}</span>
            <CostCategoryGlyphs category={band} />
            <span className="font-normal tabular-nums opacity-75">{count}</span>
          </button>
        )
      })}
    </div>
  )
}
